import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { MatSelectChange } from '@angular/material/select';
import { Store } from '@ngxs/store';
import { AuthState } from '@core/store/auth/auth.state';
import { compareLabelNames, OperationLabel } from '@core/models/operation-label.model';

export interface LabelFilterGroup {
  operationId: string;
  /** null when the options are not grouped (one operation in the header). */
  operationName: string | null;
  labels: OperationLabel[];
}

/**
 * Multi-select "Label" filter of Job Master and Invoice Management (PB-5281, PRD LF-1 to LF-5).
 * Lists active and inactive labels A to Z with a search; with "All" operations in the header
 * the options are grouped under their operation so same-named labels stay apart.
 */
@Component({
  selector: 'hmt-label-filter-select',
  templateUrl: './label-filter-select.component.html',
  styleUrls: ['./label-filter-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class LabelFilterSelectComponent implements OnChanges {
  /** Labels of the header operation(s); deleted ones are never passed in. */
  @Input() labels: OperationLabel[] = [];
  @Input() selectedIds: string[] = [];
  @Input() appearance: MatFormFieldAppearance = 'fill';
  /** Floating label of the field; null when the caller shows its own label. */
  @Input() fieldLabel: string | null = 'Label';
  @Output() selectedIdsChange = new EventEmitter<string[]>();

  groups: LabelFilterGroup[] = [];
  search = '';

  constructor(private store: Store) {}

  ngOnChanges(): void {
    this.groups = this.buildGroups();
  }

  get grouped(): boolean {
    return this.groups.length > 0 && this.groups[0].operationName !== null;
  }

  get triggerText(): string {
    const ids = this.selectedIds || [];
    if (ids.length === 1) {
      return this.labels.find(label => label.id === ids[0])?.name || '1 selected';
    }
    return `${ids.length} selected`;
  }

  /** Options outside the search stay rendered (hidden) so mat-select keeps their selection. */
  matches(label: OperationLabel): boolean {
    const term = this.search.trim().toLowerCase();
    return !term || label.name.toLowerCase().includes(term);
  }

  groupMatches(group: LabelFilterGroup): boolean {
    return group.labels.some(label => this.matches(label));
  }

  get noMatch(): boolean {
    return this.labels.length > 0 && !this.labels.some(label => this.matches(label));
  }

  onSelectionChange(event: MatSelectChange) {
    this.selectedIdsChange.emit([...(event.value || [])]);
  }

  selectAll() {
    this.selectedIdsChange.emit([]);
  }

  onOpenedChange(open: boolean) {
    if (!open) {
      this.search = '';
    }
  }

  trackById(_: number, label: OperationLabel) {
    return label.id;
  }

  private buildGroups(): LabelFilterGroup[] {
    const labels = [...(this.labels || [])].sort(compareLabelNames);
    const headerOperations = this.store.selectSnapshot(AuthState.getUser)?.operations || [];
    if (headerOperations.length === 1) {
      return labels.length ? [{operationId: headerOperations[0], operationName: null, labels}] : [];
    }
    const names = new Map((this.store.selectSnapshot(AuthState.getCurrentUserOperations) || [])
      .map(op => [op.id, op.name] as [string, string]));
    const byOperation = new Map<string, OperationLabel[]>();
    labels.forEach(label => {
      const key = label.operationId || '';
      byOperation.set(key, [...(byOperation.get(key) || []), label]);
    });
    return Array.from(byOperation.entries())
      .map(([operationId, groupLabels]) => ({
        operationId,
        operationName: names.get(operationId) || 'Other',
        labels: groupLabels
      }))
      .sort((a, b) => a.operationName.localeCompare(b.operationName, undefined, {sensitivity: 'base'}));
  }
}

/** Selected label ids that are still offered; used to drop labels of another operation (PRD LF-3). */
export function keepOfferedLabelIds(selectedIds: string[], labels: OperationLabel[]): string[] {
  const offered = new Set((labels || []).map(label => label.id));
  return (selectedIds || []).filter(id => offered.has(id));
}
